"use client";

/**
 * Send feature — flow state machine (React hook).
 *
 * Owns the multi-step Send UX (token → recipient → amount → review → result)
 * and the transaction lifecycle, delegating all logic to {@link sendUseCases}
 * via a {@link SendRepository}. Screens are pure presenters of what this
 * returns; they hold no business logic.
 */

import * as React from "react";
import { resolveEnvironment } from "../../../lib/environment";
import {
  normalizeFailureReason,
  trackDappUiFunnel,
  type TrackDappUiFunnelOptions,
} from "../../../lib/sentry/index";
import { formatWei } from "../../../lib/format";
import type {
  EstimateGasFn,
  GasEstimate,
  GetTransactionReceiptFn,
  SendTransactionFn,
} from "../../wallet-portfolio/types";
import { useTransactionExplorerUrl } from "../queries/use-chain-info";
import type { Environment } from "../../../types";
import type { OnOutlink } from "../../../lib/outlink";
import type { SendAccount, SendAsset } from "../types";
import {
  getRecentSendAddresses,
  isNativeSendAsset,
  saveRecentSendAddress,
} from "../utils";
import { getEstTxFeeWei, isBscLegacyChainId } from "./sendFormat";
import { formatSendError } from "../utils/sendErrorMessage";
import { createSendRepository } from "./SendRepository";
import {
  buildSendTxArgs,
  executeSend,
  hasFieldErrors,
  type SendFieldErrors,
  validateSend,
} from "./sendUseCases";

export type SendStep = "token" | "recipient" | "amount" | "review" | "result";
export type SendStatus =
  | "idle"
  | "submitting"
  | "confirming"
  | "success"
  | "error";

export interface SendSubmittedInfo {
  txHash: `0x${string}`;
  chainId: number;
  amount: string;
  tokenSymbol: string;
  recipient: `0x${string}`;
}

export interface SendFailedInfo {
  txHash?: `0x${string}`;
  chainId: number;
  message: string;
}

export interface UseSendFlowOptions {
  env?: Environment;
  walletAddress: string;
  accountName?: string;
  accounts?: SendAccount[];
  initialStep?: "token" | "recipient";
  /** Token preselected by the host (wallet-info token click). */
  token: SendAsset;
  tokens?: SendAsset[];
  onTokenChange?: (token: SendAsset) => void;
  sendTransaction?: SendTransactionFn;
  getTransactionReceipt?: GetTransactionReceiptFn;
  estimateGas?: EstimateGasFn;
  /** Optional USD price lookup for a token (key = chainId:address). */
  getTokenPriceUsd?: (token: SendAsset) => number | undefined;
  onSubmitted?: (info: SendSubmittedInfo) => void;
  onSuccess?: (txHash: `0x${string}`) => void;
  onFailed?: (info: SendFailedInfo) => void;
  /** Called when the user leaves the first step (back out of Send). */
  onExit?: () => void;
  onOutlink?: OnOutlink;
}

const STEP_ORDER: SendStep[] = ["token", "recipient", "amount", "review"];

export function useSendFlow(options: UseSendFlowOptions) {
  const {
    env,
    walletAddress,
    accountName,
    accounts = [],
    initialStep = "recipient",
    token: initialToken,
    tokens = [],
    onTokenChange,
    sendTransaction,
    getTransactionReceipt,
    estimateGas,
    getTokenPriceUsd,
    onSubmitted,
    onSuccess,
    onFailed,
    onExit,
    onOutlink,
  } = options;

  const repository = React.useMemo(
    () => createSendRepository({ sendTransaction, getTransactionReceipt, estimateGas }),
    [sendTransaction, getTransactionReceipt, estimateGas],
  );

  const [token, setToken] = React.useState<SendAsset>(initialToken);
  // Re-sync when the host swaps the preselected token while mounted.
  React.useEffect(() => {
    setToken(initialToken);
  }, [initialToken.address, initialToken.chainId]);

  const [step, setStep] = React.useState<SendStep>(initialStep);
  const [recipient, setRecipientState] = React.useState("");
  const [amount, setAmountState] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState<SendFieldErrors>({});
  const [status, setStatus] = React.useState<SendStatus>("idle");
  const [txHash, setTxHash] = React.useState<`0x${string}` | "">("");
  const [error, setError] = React.useState("");
  const [gasEstimate, setGasEstimate] = React.useState<GasEstimate | null>(null);
  const [gasLoading, setGasLoading] = React.useState(false);
  const [gasError, setGasError] = React.useState("");
  const gasReqIdRef = React.useRef(0);
  const [reviewRawAmount, setReviewRawAmount] = React.useState<bigint | null>(null);
  const [reviewRecipient, setReviewRecipient] = React.useState<`0x${string}` | "">("");
  const [recentAddresses, setRecentAddresses] = React.useState(() =>
    getRecentSendAddresses(walletAddress),
  );

  const funnelFiredRef = React.useRef(new Set<string>());
  const trackFunnelOnce = React.useCallback(
    (funnelStep: string, funnelOptions?: TrackDappUiFunnelOptions) => {
      if (funnelFiredRef.current.has(funnelStep)) return;
      funnelFiredRef.current.add(funnelStep);
      trackDappUiFunnel("send", funnelStep, funnelOptions);
    },
    [],
  );

  React.useEffect(() => {
    trackFunnelOnce("opened");
    if (initialStep !== "token") {
      trackFunnelOnce("token_selected", { tags: { item_id: initialToken.symbol } });
    }
  }, [trackFunnelOnce, initialStep, initialToken.symbol]);

  const resolvedEnv = React.useMemo(() => resolveEnvironment(env), [env]);
  const { transactionUrl, chainInfo } = useTransactionExplorerUrl(
    resolvedEnv,
    token.chainId,
    txHash || undefined,
    true,
  );
  const nativeSymbol = chainInfo?.currency_symbol ?? "";
  const networkName = chainInfo?.name ?? `Chain ID ${token.chainId}`;

  // Error-message context for gas estimation / send. Held in a ref so the
  // gas-estimate effect does NOT re-run (and cancel its in-flight request)
  // when chainInfo resolves and flips nativeSymbol / networkName — that churn
  // previously left the estimate stuck on its loading state forever.
  const errorCtxRef = React.useRef({
    nativeSymbol: nativeSymbol || undefined,
    networkName,
    tokenSymbol: token.symbol,
  });
  errorCtxRef.current = {
    nativeSymbol: nativeSymbol || undefined,
    networkName,
    tokenSymbol: token.symbol,
  };

  const balanceRaw = React.useMemo(
    () => BigInt(token.quantity.numeric || "0"),
    [token.quantity.numeric],
  );
  const balanceText = React.useMemo(
    () => formatWei(token.quantity.numeric, token.quantity.decimals),
    [token.quantity.numeric, token.quantity.decimals],
  );
  const priceUsd = getTokenPriceUsd?.(token);
  const amountUsd = React.useMemo(() => {
    if (priceUsd === undefined) return undefined;
    const n = parseFloat(amount.replace(/,/g, ""));
    return Number.isFinite(n) ? n * priceUsd : 0;
  }, [amount, priceUsd]);

  // Reset transient state when the token changes.
  React.useEffect(() => {
    setAmountState("");
    setFieldErrors({});
  }, [token.address, token.chainId]);

  // 입력(토큰/수신자/금액)이 바뀌면 진행 중이던 가스 추정을 무효화한다
  // (develop SendPage 와 동일). reqId 를 올려 in-flight 결과가 다음 화면에
  // stale 하게 안착하는 것을 막는다. cleanup 대신 이 무효화로만 staleness 를 관리.
  React.useEffect(() => {
    gasReqIdRef.current += 1;
    setGasEstimate(null);
    setGasError("");
    setGasLoading(false);
  }, [token.address, token.chainId, recipient, amount]);

  React.useEffect(() => {
    setRecentAddresses(getRecentSendAddresses(walletAddress));
  }, [walletAddress]);

  // ── Actions ──────────────────────────────────────────────────────────
  const setRecipient = React.useCallback((value: string) => {
    setRecipientState(value);
    setFieldErrors((prev) => ({ ...prev, recipient: undefined }));
  }, []);

  const setAmount = React.useCallback((value: string) => {
    setAmountState(value);
    setFieldErrors((prev) => ({ ...prev, amount: undefined }));
  }, []);

  const setMax = React.useCallback(() => {
    // Fill the *displayed* (floor-truncated) balance, not full precision — so
    // the amount the user sends matches what the Balance row shows. Strip the
    // thousands separators formatWei adds so the value stays parseable.
    setAmountState(balanceText.replace(/,/g, ""));
    setFieldErrors((prev) => ({ ...prev, amount: undefined }));
  }, [balanceText]);

  const selectToken = React.useCallback(
    (next: SendAsset) => {
      setToken(next);
      onTokenChange?.(next);
      trackFunnelOnce("token_selected", { tags: { item_id: next.symbol } });
      setStep("recipient");
    },
    [onTokenChange, trackFunnelOnce],
  );

  const goToTokenSelect = React.useCallback(() => setStep("token"), []);

  const back = React.useCallback(() => {
    setStep((current) => {
      if (current === "result") return "review";
      if (current === initialStep) {
        onExit?.();
        return current;
      }
      const idx = STEP_ORDER.indexOf(current);
      if (idx <= 0) {
        onExit?.();
        return current;
      }
      return STEP_ORDER[idx - 1];
    });
  }, [initialStep, onExit]);

  // recipient → amount
  const submitRecipient = React.useCallback(() => {
    const { errors } = validateSend({
      recipient,
      amount: "1", // amount validated on its own step; only check recipient here
      token,
      balanceRaw,
    });
    if (errors.recipient) {
      setFieldErrors((prev) => ({ ...prev, recipient: errors.recipient }));
      return;
    }
    setFieldErrors((prev) => ({ ...prev, recipient: undefined }));
    trackFunnelOnce("recipient_entered");
    setStep("amount");
  }, [recipient, token, balanceRaw, trackFunnelOnce]);

  const selectRecipient = React.useCallback(
    (address: `0x${string}`) => {
      setRecipientState(address);
      setFieldErrors((prev) => ({ ...prev, recipient: undefined }));
      trackFunnelOnce("recipient_entered");
      setStep("amount");
    },
    [trackFunnelOnce],
  );

  // amount → review
  const submitAmount = React.useCallback(() => {
    const result = validateSend({ recipient, amount, token, balanceRaw });
    setFieldErrors(result.errors);
    if (
      hasFieldErrors(result.errors) ||
      result.rawAmount === undefined ||
      !result.recipient
    ) {
      return;
    }
    setReviewRawAmount(result.rawAmount);
    setReviewRecipient(result.recipient);
    setGasEstimate(null);
    setGasError("");
    setStatus("idle");
    trackFunnelOnce("amount_entered");
    setStep("review");
  }, [recipient, amount, token, balanceRaw, trackFunnelOnce]);

  // Review 단계에서 가스 추정을 한 번 트리거한다. chainInfo 가 아직 로딩 중이면
  // 효과가 다시 돌면서 자동으로 늦게 발사된다.
  //
  // 중요: cleanup / cancelled 플래그를 두지 않는다 (develop 의 SendPage 와 동일).
  // staleness 는 오직 gasReqIdRef 로만 판별한다. chainInfo 가 비동기로 로드되며
  // 의존성이 바뀌어도, 진행 중인 추정 요청을 취소하면 안 된다 — 가드(gasLoading)
  // 가 새 요청을 막는 사이 in-flight 요청만 버려져 UI 가 "…" 에 영구히 갇힌다.
  React.useEffect(() => {
    if (step !== "review") return;
    if (!reviewRecipient || reviewRawAmount === null) return;
    if (gasEstimate !== null || gasLoading || gasError) return;
    if (!repository.canEstimateGas(chainInfo?.rpc)) return;

    const reqId = ++gasReqIdRef.current;
    setGasLoading(true);
    setGasError("");
    void (async () => {
      try {
        const args = buildSendTxArgs({
          token,
          walletAddress,
          recipient: reviewRecipient,
          rawAmount: reviewRawAmount,
        });
        const estimate = await repository.estimateGas(args, chainInfo?.rpc);
        if (gasReqIdRef.current !== reqId) return;
        setGasEstimate(estimate);
      } catch (err) {
        if (gasReqIdRef.current !== reqId) return;
        setGasError(
          formatSendError(err, errorCtxRef.current, "Failed to estimate gas."),
        );
      } finally {
        if (gasReqIdRef.current === reqId) setGasLoading(false);
      }
    })();
  }, [
    step,
    reviewRecipient,
    reviewRawAmount,
    gasEstimate,
    gasLoading,
    gasError,
    repository,
    chainInfo?.rpc,
    token,
    walletAddress,
  ]);

  // Gas affordability — native sends only. The fee is paid in the same coin
  // being transferred, so `amount + est. fee` must fit the balance. ERC-20
  // sends pay gas in the native coin (a different balance we don't hold here),
  // so this check doesn't apply to them.
  const insufficientGas = React.useMemo(() => {
    if (!isNativeSendAsset(token)) return false;
    if (!gasEstimate || reviewRawAmount === null) return false;
    const feeWei = getEstTxFeeWei(gasEstimate, isBscLegacyChainId(token.chainId));
    if (feeWei === null) return false;
    return reviewRawAmount + feeWei > balanceRaw;
  }, [token, gasEstimate, reviewRawAmount, balanceRaw]);

  const confirmSend = React.useCallback(async () => {
    if (!reviewRecipient || reviewRawAmount === null) return;
    if (insufficientGas) return;
    setError("");
    setTxHash("");
    setStatus("submitting");
    trackFunnelOnce("submitted");
    try {
      let submittedHash: `0x${string}` | "" = "";
      const outcome = await executeSend({
        repository,
        args: buildSendTxArgs({
          token,
          walletAddress,
          recipient: reviewRecipient,
          rawAmount: reviewRawAmount,
        }),
        chainId: token.chainId,
        onSubmitted: (hash) => {
          submittedHash = hash;
          setTxHash(hash);
          setStatus("confirming");
          setRecentAddresses(saveRecentSendAddress(walletAddress, reviewRecipient));
          onSubmitted?.({
            txHash: hash,
            chainId: token.chainId,
            amount,
            tokenSymbol: token.symbol,
            recipient: reviewRecipient,
          });
        },
      });
      if (outcome === "success") {
        setStatus("success");
        trackFunnelOnce("confirmed");
        if (submittedHash && onSuccess) {
          onSuccess?.(submittedHash);
        } else {
          setStep("result");
        }
      } else if (outcome === "reverted") {
        const message = "Transaction reverted on-chain.";
        setStatus("error");
        setError(message);
        trackFunnelOnce("confirmed", {
          status: "failure",
          reason: normalizeFailureReason(new Error(message)),
        });
        if (submittedHash && onFailed) {
          onFailed({ txHash: submittedHash, chainId: token.chainId, message });
        } else {
          setStep("result");
        }
      } else {
        const message = "Transaction receipt was not found. Please check again later.";
        setStatus("error");
        setError(message);
        trackFunnelOnce("confirmed", { status: "failure", reason: "timeout" });
        if (submittedHash && onFailed) {
          onFailed({ txHash: submittedHash, chainId: token.chainId, message });
        } else {
          setStep("result");
        }
      }
    } catch (err) {
      setStatus("error");
      trackFunnelOnce("confirmed", {
        status: "failure",
        reason: normalizeFailureReason(err),
      });
      setError(
        formatSendError(err, {
          nativeSymbol: nativeSymbol || undefined,
          networkName,
          tokenSymbol: token.symbol,
        }),
      );
      setStep("result");
    }
  }, [
    repository,
    reviewRecipient,
    reviewRawAmount,
    insufficientGas,
    token,
    amount,
    walletAddress,
    onSubmitted,
    onSuccess,
    onFailed,
    nativeSymbol,
    networkName,
    trackFunnelOnce,
  ]);

  const [hashCopied, setHashCopied] = React.useState(false);
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  React.useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const copyHash = React.useCallback(async () => {
    if (!txHash) return;
    try {
      await navigator.clipboard.writeText(txHash);
      setHashCopied(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setHashCopied(false), 2000);
    } catch {
      /* copy is best-effort */
    }
  }, [txHash]);

  const openTransaction = React.useCallback(() => {
    if (!transactionUrl || !txHash) return;
    const openTab = (target: string) => {
      if (typeof window !== "undefined") {
        window.open(target, "_blank", "noopener,noreferrer");
      }
    };
    if (!onOutlink) {
      openTab(transactionUrl);
      return;
    }
    const maybe = onOutlink(transactionUrl, {
      category: "send",
      origin: "send-transaction",
      payload: { chainId: token.chainId, txHash },
    });
    if (maybe && typeof (maybe as { then?: unknown }).then === "function") {
      const win =
        typeof window !== "undefined"
          ? window.open("", "_blank", "noopener,noreferrer")
          : null;
      (maybe as Promise<string | null | undefined>).then((result) => {
        if (result === null) {
          win?.close();
          return;
        }
        const target = result ?? transactionUrl;
        if (win) {
          try {
            win.location.href = target;
          } catch {
            win.close();
            openTab(target);
          }
        } else {
          openTab(target);
        }
      });
      return;
    }
    const result = maybe as string | null | undefined;
    if (result !== null) openTab(result ?? transactionUrl);
  }, [onOutlink, token.chainId, transactionUrl, txHash]);

  const retry = React.useCallback(() => {
    setError("");
    setTxHash("");
    setStatus("idle");
    setGasEstimate(null);
    setGasError("");
    funnelFiredRef.current.delete("submitted");
    funnelFiredRef.current.delete("confirmed");
    setStep("review");
  }, []);

  return {
    // state
    step,
    status,
    token,
    tokens: tokens.length > 0 ? tokens : [token],
    recipient,
    amount,
    amountUsd,
    fieldErrors,
    error,
    txHash,
    gasEstimate,
    gasLoading,
    gasError,
    insufficientGas,
    reviewRawAmount,
    reviewRecipient,
    recentAddresses,
    accounts,
    accountName,
    walletAddress,
    resolvedEnv,
    transactionUrl,
    networkName,
    nativeSymbol,
    nativeDecimals: chainInfo?.currency_decimals ?? token.quantity.decimals,
    balanceRaw,
    balanceText,
    priceUsd,
    canSelectToken: (tokens.length > 0 ? tokens : [token]).length > 1,
    canSend: repository.canSend,
    getTokenPriceUsd,
    // actions
    setRecipient,
    setAmount,
    setMax,
    selectToken,
    goToTokenSelect,
    selectRecipient,
    submitRecipient,
    submitAmount,
    confirmSend,
    retry,
    back,
    hashCopied,
    copyHash,
    openTransaction,
  };
}

export type SendFlow = ReturnType<typeof useSendFlow>;
